import React, { useEffect, useState } from 'react';
// Added Link to imports
import { Link } from 'react-router-dom';
import { Car, CarStatus } from '../types';
import { dataService } from '../services/dataService';
import { CarCard } from '../components/CarCard';
import { Filter, Search, X, ChevronDown, Grid, List } from 'lucide-react';

export const Inventory: React.FC = () => {
  const [cars, setCars] = useState<Car[]>([]);
  const [filteredCars, setFilteredCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('All');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await dataService.getCars();
      setCars(data);
      setFilteredCars(data);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    let result = cars;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(c => 
        c.make.toLowerCase().includes(lower) || 
        c.model.toLowerCase().includes(lower) || 
        c.year.toString().includes(lower)
      );
    }

    if (selectedMake !== 'All') {
      result = result.filter(c => c.make === selectedMake);
    }

    if (minPrice) {
      result = result.filter(c => c.price >= parseInt(minPrice));
    }
    if (maxPrice) {
      result = result.filter(c => c.price <= parseInt(maxPrice));
    }

    if (statusFilter !== 'All') {
      result = result.filter(c => c.status === statusFilter);
    }

    setFilteredCars(result);
  }, [searchTerm, selectedMake, minPrice, maxPrice, statusFilter, cars]);

  const uniqueMakes = ['All', ...Array.from(new Set(cars.map(c => c.make)))];

  return (
    <div className="bg-brandGray min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb / Header */}
        <div className="mb-12">
          <h1 className="font-heading text-4xl font-bold uppercase text-brandDark mb-2">Pre-Owned <span className="text-primary">Inventory</span></h1>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span>/</span>
            <span className="text-brandDark font-bold">Inventory</span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="bg-white p-6 shadow-sm border border-gray-200 sticky top-28">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl font-bold uppercase">Filter Results</h2>
                <Filter size={20} className="text-primary" />
              </div>

              <div className="space-y-8">
                {/* Search */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-3 text-gray-400">Keywords</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search make or model..."
                      className="w-full bg-brandGray border-b-2 border-transparent focus:border-primary px-4 py-3 text-sm focus:outline-none transition-colors"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search className="absolute right-3 top-3.5 text-gray-300" size={18} />
                  </div>
                </div>

                {/* Make Select */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-3 text-gray-400">Manufacturer</label>
                  <select 
                    value={selectedMake} 
                    onChange={(e) => setSelectedMake(e.target.value)}
                    className="w-full bg-brandGray border-b-2 border-transparent focus:border-primary px-4 py-3 text-sm focus:outline-none cursor-pointer"
                  >
                    {uniqueMakes.map(make => <option key={make} value={make}>{make}</option>)}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-3 text-gray-400">Price Range</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      placeholder="Min $"
                      className="bg-brandGray border-b-2 border-transparent focus:border-primary px-4 py-3 text-sm focus:outline-none w-full"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Max $"
                      className="bg-brandGray border-b-2 border-transparent focus:border-primary px-4 py-3 text-sm focus:outline-none w-full"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-3 text-gray-400">Availability</label>
                  <div className="space-y-3">
                    {['All', CarStatus.AVAILABLE, CarStatus.PENDING].map((status) => (
                      <label key={status} className="flex items-center gap-3 group cursor-pointer">
                        <input 
                          type="radio" 
                          name="status" 
                          value={status}
                          checked={statusFilter === status}
                          onChange={() => setStatusFilter(status)}
                          className="w-4 h-4 border-2 border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className={`text-sm font-medium transition-colors ${statusFilter === status ? 'text-primary font-bold' : 'text-gray-600 group-hover:text-brandDark'}`}>
                          {status === 'All' ? 'View All Statuses' : status}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={() => {
                    setSelectedMake('All');
                    setMinPrice('');
                    setMaxPrice('');
                    setStatusFilter('All');
                    setSearchTerm('');
                  }}
                  className="w-full py-4 bg-brandDark text-white font-heading text-sm font-bold uppercase tracking-widest hover:bg-primary transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-grow">
            {/* Toolbar */}
            <div className="bg-white p-4 border border-gray-200 shadow-sm mb-8 flex justify-between items-center">
              <p className="text-sm font-medium text-gray-500">
                Displaying <span className="text-brandDark font-bold">{filteredCars.length}</span> results
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold uppercase text-gray-400 hidden sm:inline">Sort By:</span>
                <select className="text-sm font-bold bg-transparent focus:outline-none cursor-pointer">
                  <option>Newest Arrivals</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Mileage: Lowest</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center h-96">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-brandGray border-t-primary mb-4"></div>
                <p className="font-heading font-bold uppercase tracking-widest text-gray-400">Searching Inventory...</p>
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="bg-white rounded-sm p-20 text-center border border-dashed border-gray-300">
                <X size={64} className="text-gray-200 mx-auto mb-6" />
                <h3 className="font-heading text-3xl font-bold uppercase mb-2">No Matching Vehicles</h3>
                <p className="text-gray-500 mb-8">Try adjusting your filters or search keywords to find what you're looking for.</p>
                <button 
                  onClick={() => {
                    setSelectedMake('All');
                    setMinPrice('');
                    setMaxPrice('');
                    setStatusFilter('All');
                    setSearchTerm('');
                  }}
                  className="bg-primary text-white font-heading px-10 py-4 uppercase font-bold tracking-widest"
                >
                  Browse Full Collection
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredCars.map(car => (
                  <CarCard key={car.id} car={car} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};